import { languageSelector } from "@/src/contexts";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import ImageLoader from "@Components/ImageLoader";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { ITextbox } from "@Components/Textbox";

import type { IButtonsBar } from "../common/ButtonsBar";
import { ButtonsBar } from "../common/ButtonsBar";
import type { INavigationModalActions } from "../common/NavigationModal";
import type { IInputTracker } from "../telemetry/InputTracker";
import { TextboxTracker } from "../telemetry/InputTracker";
import type { ICashbackClaimSummaryResources } from "./CashbackClaimSummary.Resources";
import { CashbackClaimSummaryResources } from "./CashbackClaimSummary.Resources";
import { CashbackClaimSummaryResourcesEn } from "./CashbackClaimSummary.Resources.En";
import type { ICashbackClaimFields } from "./CashbackCommonInterfaces";
import { AvailabilityState, CashbackClaimType, type ComplaintType } from "./CashbackCommonInterfaces";

import * as css from "./CashbackClaimSummary.module.css";

export interface ICashbackClaimSummary
    extends INavigationModalActions<ICashbackClaimFields>, IComponentAjax<boolean> {
    title: string;
    reasonLabel: string;
    reasonWrongAmountLabel?: string;
    reasonWrongRejectLabel?: string;
    reasonMissingClaimLabel?: string;
    cashbackAmountLabel?: string;
    cashbackNewAmountLabel?: string;
    transactionInfoLabel: string;
    purchaseAmountLabel: string;
    dateLabel: string;
    remarksLabel?: string;
    remarksTextbox?: IInputTracker<ITextbox>;
    buttonsBar: IButtonsBar;
}

interface ICashbackClaimSummaryState extends IComponentAjaxState {
    remark: string | null;
}

class CashbackClaimSummary extends ComponentAjax<
    boolean,
    ICashbackClaimSummary & ICashbackClaimSummaryResources,
    ICashbackClaimSummaryState
> {
    public static readonly defaultProps: Partial<
        ICashbackClaimSummary & ICashbackClaimSummaryResources
    > = {
        ...ComponentAjax.defaultProps,
        ajaxMethod: AjaxMethod.Post,
    };

    private steps: number = 1;

    private claimTypeLabel: string | undefined;

    private claimType: ComplaintType = "Unknown";

    private css: string = "";

    private isSubmitted: boolean = false;

    constructor(props: ICashbackClaimSummary & ICashbackClaimSummaryResources) {
        super(props);

        this.initializeModules();

        this.state = {
            isLoading: false,
            remark: null,
        };
    }

    private initializeModules = () => {
        switch (this.props.fieldValues && this.props.fieldValues.availability) {
            case AvailabilityState.Rejected: {
                this.steps = 2;
                this.claimTypeLabel = this.props.reasonWrongRejectLabel;
                this.claimType = "CashbackRejected";
                this.css = css.rejected;
                break;
            }

            case AvailabilityState.Pending: {
                this.claimTypeLabel = this.props.reasonWrongAmountLabel;
                this.claimType = "IncorrectCashback";
                this.css = css.pending;
                break;
            }
            case AvailabilityState.Approved: {
                this.claimTypeLabel = this.props.reasonWrongAmountLabel;
                this.claimType = "IncorrectCashback";
                this.css = css.approved;
                break;
            }
            case AvailabilityState.Unknown:
            default: {
                this.steps = 2;
                this.claimTypeLabel = this.props.reasonMissingClaimLabel;
                this.claimType =
                    this.props.fieldValues &&
                    this.props.fieldValues.cashbackClaimType === CashbackClaimType.Visa
                        ? "MissingVisaTransaction"
                        : "MissingTransaction";
                this.css = css.pending;
                break;
            }
        }
    };

    public render() {
        const buttonsBar: IButtonsBar = this.props.buttonsBar && {
            ...this.props.buttonsBar,
            nextButton: {
                ...this.props.buttonsBar.nextButton,
                onClick: this.submitForm,
            },
            cancelButton: {
                ...this.props.buttonsBar.cancelButton,
                onClick: this.previousPage,
            },
        };

        return (
            <div {...defaultAttributes(this.props, css.cashbackClaimSummaryComponent, css.standardModalForm)}>
                <h1 className={css.standardModalTitle}>{this.props.title}</h1>
                <div className={css.standardModalFormBody}>
                    {this.getClaimInfo()}
                    {this.getTransactionInfo()}
                    {this.getRemark()}
                </div>
                <ButtonsBar {...buttonsBar} />
            </div>
        );
    }

    protected ajaxSuccess(response: boolean): void {
        this.setState(() => ({ isLoading: false }));

        if (response) {
            if (this.props.onSubmit) {
                this.props.onSubmit();
            }
        }
    }

    protected ajaxFail(): boolean {
        this.setState(() => ({ isLoading: false }));

        return true;
    }

    private getTransactionInfo = () =>
        this.props.fieldValues && (
            <div className={css.transactionDetails}>
                <label className={css.standardModalLabel}>{this.props.transactionInfoLabel}</label>
                <div className={css.transactionImage}>
                    <ImageLoader src={this.props.fieldValues.imageUrl || ""} className={css.image} />
                </div>
                <div className={css.transactionDate}>
                    <label>{this.props.fieldValues.brandName}</label>
                    {this.props.fieldValues &&
                        this.props.fieldValues.cashbackClaimType !== CashbackClaimType.Visa &&
                        this.labelDescription(this.props.fieldValues.orderId)}
                    {this.labelDescription(this.props.fieldValues.date, this.props.dateLabel)}
                    {this.labelDescription(
                        this.props.fieldValues.purchaseAmount,
                        this.props.purchaseAmountLabel,
                    )}
                    {this.props.fieldValues &&
                        this.props.fieldValues.cashbackClaimType === CashbackClaimType.Visa &&
                        this.labelDescription(
                            `${this.props.purchaseLabel}${this.props.fieldValues.visaChannelType}`,
                        )}
                </div>
                {this.getCaskbackAmount()}
            </div>
        );

    private getRemark = () => (
        <div className={css.remark}>
            <label className={css.standardModalLabel}>{this.props.remarksLabel}</label>
            <TextboxTracker
                {...this.props.remarksTextbox}
                focusTelemetryCustom={{
                    eventName: "cshbck-ClmMakeSummaryEdit",
                    actionType: "Undefined",
                }}
                properties={{
                    ...(this.props.remarksTextbox && this.props.remarksTextbox.properties),
                    onChange: (event) => this.setState({ remark: event.target.value }),
                    maxLength: 200,
                    value: this.state.remark || "",
                }}
            />
        </div>
    );

    private getCaskbackAmount = () =>
        this.props.fieldValues &&
        this.props.fieldValues.cashbackAmount && (
            <>
                <div className={css.availability}>
                    <span className={`${css.availabilityType} ${this.css}`} />
                </div>
                <div className={css.transactionAmount}>
                    <label>{this.props.cashbackAmountLabel}</label>
                    <span>{this.props.fieldValues.cashbackAmount}</span>
                </div>
            </>
        );

    private getNewCashbackAmount = () =>
        this.props.fieldValues &&
        this.props.fieldValues.cashbackNewAmount && (
            <div className={css.newCashbackAmount}>
                <label className={css.standardModalLabel}>{this.props.cashbackNewAmountLabel}</label>
                <span>{this.props.fieldValues.cashbackNewAmount}</span>
            </div>
        );

    private getClaimInfo = () => (
        <div className={css.claimDetails}>
            <div className={css.reason}>
                <label className={css.standardModalLabel}>{this.props.reasonLabel}</label>
                <div className={css.claimTypeLabel}>{this.claimTypeLabel}</div>
            </div>
            {this.getNewCashbackAmount()}
        </div>
    );

    private labelDescription = (description?: string, label?: string) =>
        !label ? (
            <div className={css.labelDescription}>
                <span>{description}</span>
            </div>
        ) : (
            <div className={css.labelDescription}>
                <span className={css.label}>{label}</span>
                <span>{description}</span>
            </div>
        );

    private previousPage = () => {
        if (this.props.previousPage) {
            this.props.previousPage(this.steps);
        }
    };

    private submitForm = () => {
        if (this.isSubmitted) {
            return;
        }

        this.isSubmitted = true;
        const fields = this.props.fieldValues;

        if (!fields) {
            return;
        }

        this.ajaxRequest(
            {
                complaintType: this.claimType,
                transactionId: fields.transactionId,
                brandId: fields.brandId,
                orderId: fields.orderId,
                cashbackAmount: fields.cashbackNewAmountValue,
                purchaseAmount: fields.purchaseAmountValue,
                transactionDate: fields.dateFormat ? fields.dateFormat.toISOString() : null,
                description: this.state.remark,
                cashbackClaimType: fields.cashbackClaimType,
                visaChannelType: fields.visaChannelType,
            },
            () => {
                this.setState({ isLoading: true });
            },
        );
    };
}

const CashbackClaimSummaryConnect = (props: ICashbackClaimSummary) => {
    const resources = languageSelector(CashbackClaimSummaryResources, CashbackClaimSummaryResourcesEn);

    return <CashbackClaimSummary {...{ ...props, ...resources }} />;
};

export { CashbackClaimSummaryConnect as CashbackClaimSummary };
